import { useEffect, useState } from "react";
import * as ReactRouter from "react-router";
import { cn } from "~/app/lib/utils";

/**
 * A delicate horizontal loading bar that appears at the top of the viewport
 * when React Router is loading data.
 */
export function NavigationProgress() {
  const navigation = ReactRouter.useNavigation();
  const isLoading = navigation.state === "loading";
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isLoading) {
      setVisible(true);
      setProgress(0);

      // Simulate progress - starts fast, slows down as it approaches 90%
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev;
          const increment = Math.max(0.5, (90 - prev) * 0.05);
          return Math.min(90, prev + increment);
        });
      }, 100);

      return () => clearInterval(interval);
    } else {
      // Complete the progress bar
      setProgress(100);

      // Hide after animation completes
      const timeout = setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 200);

      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  if (!visible && !isLoading) return null;

  return (
    <div
      className={cn(
        "fixed top-0 left-0 right-0 z-[9999] h-[3px]",
        "pointer-events-none",
        !visible && "opacity-0"
      )}
      role="progressbar"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={cn("h-full transition-all duration-200 ease-out")}
        style={{
          width: `${progress}%`,
          background:
            "linear-gradient(to right, #0E5938, rgba(14, 89, 56, 0.8), #0E5938)",
          transition: isLoading
            ? "width 100ms ease-out"
            : "width 200ms ease-out, opacity 200ms ease-out",
          boxShadow:
            "0 0 15px rgba(14, 89, 56, 0.8), 0 0 5px rgba(14, 89, 56, 1)",
        }}
      />
    </div>
  );
}
